import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';

/**
 * Componente para visualização e gerenciamento de logs de erros do sistema.
 * 
 * TODO: Melhorias futuras planejadas:
 * - Implementar integração com sistema de logs real (ELK Stack ou similar)
 * - Adicionar gráficos e estatísticas para análise de tendências de erros
 * - Melhorar a interface com filtros avançados e visualização em tempo real
 * - Implementar sistema de alertas para erros críticos
 * - Adicionar funcionalidade de agrupamento de erros similares
 */
const ErrorLogs = () => {
  const [logs, setLogs] = useState({ errors: [], success: [] });
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('errors');
  const [filter, setFilter] = useState('');

  // Estado para controlar o modal de detalhes do erro
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    fetchSystemLogs();
  }, []);

  // Função para adicionar entradas ao log
  const addToLog = (type, message) => {
    const logEntry = {
      timestamp: new Date(),
      message,
      details: typeof message === 'object' ? JSON.stringify(message) : message
    };
    
    setLogs(prevLogs => {
      // Garantir que prevLogs seja um objeto válido
      const safeObj = prevLogs || {};
      
      // Garantir que prevLogs[type] seja um array
      const existingLogs = Array.isArray(safeObj[type]) ? safeObj[type] : [];
      
      return {
        ...safeObj,
        [type]: [logEntry, ...existingLogs].slice(0, 100) // Manter apenas os 100 logs mais recentes
      };
    });
  };

  // Função para buscar logs do sistema
  const fetchSystemLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Tentar buscar logs do backend
      try {
        const response = await axios.get('/api/admin/logs', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.logs) {
          setLogs(response.data.logs);
        }
      } catch (backendError) {
        console.error('Erro ao buscar logs do backend:', backendError);
        
        // Se falhar, usar logs mockados para demonstração
        const mockLogs = {
          errors: [
            { 
              id: 'err-001',
              timestamp: new Date(), 
              message: 'Erro ao conectar com o banco de dados', 
              details: 'Timeout após 30 segundos',
              source: 'database',
              stackTrace: 'Error: connect ETIMEDOUT\n    at Connection._handleConnectTimeout (/app/node_modules/mysql/lib/Connection.js:406:13)\n    at Object.onceWrapper (events.js:421:28)',
              dockerLogLink: '/admin/docker-logs?container=investment-db&timestamp=' + new Date().toISOString(),
              relatedErrors: ['err-003']
            },
            { 
              id: 'err-002',
              timestamp: new Date(Date.now() - 3600000), 
              message: 'Erro ao processar simulação', 
              details: 'ID da simulação inválido',
              source: 'application',
              stackTrace: 'TypeError: Cannot read property \'id\' of undefined\n    at processSimulation (/app/src/services/simulation.js:45:20)\n    at async router.post (/app/src/routes/simulation.js:28:23)',
              dockerLogLink: '/admin/docker-logs?container=investment-backend&timestamp=' + new Date(Date.now() - 3600000).toISOString(),
              relatedErrors: []
            },
            { 
              id: 'err-003',
              timestamp: new Date(Date.now() - 7200000), 
              message: 'Erro ao enviar email', 
              details: 'Configurações de SMTP inválidas',
              source: 'email',
              stackTrace: 'Error: Invalid login: 535 5.7.8 Error: authentication failed\n    at SMTPConnection._formatError (/app/node_modules/nodemailer/lib/smtp-connection/index.js:774:19)',
              dockerLogLink: '/admin/docker-logs?container=investment-backend&timestamp=' + new Date(Date.now() - 7200000).toISOString(),
              relatedErrors: ['err-001']
            },
            { 
              id: 'err-004',
              timestamp: new Date(Date.now() - 10800000), 
              message: 'Erro ao processar retirada', 
              details: 'Saldo insuficiente',
              source: 'application',
              stackTrace: 'Error: Insufficient balance for withdrawal\n    at processWithdrawal (/app/src/services/withdrawal.js:78:11)\n    at async router.post (/app/src/routes/withdrawal.js:42:18)',
              dockerLogLink: '/admin/docker-logs?container=investment-backend&timestamp=' + new Date(Date.now() - 10800000).toISOString(),
              relatedErrors: []
            }
          ],
          success: [
            { timestamp: new Date(), message: 'Simulação criada com sucesso', details: 'ID: 123' },
            { timestamp: new Date(Date.now() - 1800000), message: 'Usuário autenticado', details: 'admin@example.com' },
            { timestamp: new Date(Date.now() - 5400000), message: 'Email enviado com sucesso', details: 'Para: cliente@exemplo.com' },
            { timestamp: new Date(Date.now() - 9000000), message: 'Retirada processada', details: 'Valor: R$ 1.500,00' }
          ]
        };
        
        // Para fins de demonstração, usamos os logs mockados
        setLogs(prevLogs => {
          // Garantir que prevLogs seja um objeto válido
          const safeObj = prevLogs || {};
          
          // Garantir que prevLogs.errors e prevLogs.success sejam arrays
          const existingErrors = Array.isArray(safeObj.errors) ? safeObj.errors : [];
          const existingSuccess = Array.isArray(safeObj.success) ? safeObj.success : [];
          
          return {
            errors: [...mockLogs.errors, ...existingErrors],
            success: [...mockLogs.success, ...existingSuccess]
          };
        });
      }
    } catch (error) {
      console.error('Erro ao buscar logs do sistema:', error);
      toast.error('Erro ao carregar logs do sistema. Tente novamente.');
      
      addToLog('error', `Erro ao buscar logs do sistema: ${error.message}`);
    } finally {
      setLogsLoading(false);
    }
  };

  // Função para limpar os logs
  const clearLogs = (type) => {
    if (window.confirm(`Tem certeza que deseja limpar todos os logs de ${type === 'errors' ? 'erros' : 'sucesso'}?`)) {
      setLogs(prevLogs => ({
        ...prevLogs,
        [type]: []
      }));
      toast.success(`Logs de ${type === 'errors' ? 'erros' : 'sucesso'} limpos com sucesso.`);
    }
  };

  // Função para exportar logs
  const exportLogs = (type) => {
    try {
      const logsToExport = logs[type] || [];
      const dataStr = JSON.stringify(logsToExport, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
      
      const exportFileDefaultName = `logs_${type}_${new Date().toISOString().slice(0, 10)}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      toast.success(`Logs de ${type === 'errors' ? 'erros' : 'sucesso'} exportados com sucesso.`);
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      toast.error('Erro ao exportar logs. Tente novamente.');
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Filtrar logs
  const filteredLogs = (type) => {
    const logsArray = Array.isArray(logs[type]) ? logs[type] : [];
    if (!filter) return logsArray;
    
    return logsArray.filter(log => 
      (log.message && log.message.toLowerCase().includes(filter.toLowerCase())) ||
      (log.details && log.details.toLowerCase().includes(filter.toLowerCase()))
    );
  };

  // Função para abrir o modal de detalhes do erro
  const openErrorDetails = (error) => {
    setSelectedError(error);
  };

  // Função para fechar o modal de detalhes do erro
  const closeErrorDetails = () => {
    setSelectedError(null);
  };

  // Função para visualizar logs do Docker
  const viewDockerLogs = (containerName, timestamp) => {
    // Em um ambiente real, isso redirecionaria para uma página de logs do Docker
    // ou faria uma chamada API para buscar os logs
    toast.info(`Buscando logs do Docker para o container ${containerName} em ${formatDate(timestamp)}`);
    
    // Simulação de abertura de logs do Docker em uma nova aba
    window.open(`/admin/docker-logs?container=${containerName}&timestamp=${timestamp}`, '_blank');
  };

  // Função para extrair o nome do container a partir do link
  const getContainerName = (dockerLogLink) => {
    if (!dockerLogLink) return '';
    const match = dockerLogLink.match(/container=([^&]+)/);
    return match ? match[1] : '';
  };

  // Função para encontrar erros relacionados
  const findRelatedErrors = (errorIds) => {
    if (!Array.isArray(errorIds) || errorIds.length === 0) return [];
    
    const errorsArray = Array.isArray(logs.errors) ? logs.errors : [];
    return errorsArray.filter(error => errorIds.includes(error.id));
  };

  return (
    <MainLayout>
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Logs do Sistema</h2>
              <div className="text-muted mt-1">Visualize e gerencie os logs de erros e sucessos do sistema</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={fetchSystemLogs}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M4 4v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-8.172a2 2 0 0 0 -.586 -1.414l-5.828 -5.828a2 2 0 0 0 -1.414 -.586h-6.172a2 2 0 0 0 -2 2z" />
                    <path d="M12 3v7l2 -2l2 2v-7" />
                  </svg>
                  Atualizar Logs
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeTab === 'errors' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveTab('errors'); }}
                >
                  Erros
                  <span className="badge bg-red ms-1">{Array.isArray(logs.errors) ? logs.errors.length : 0}</span>
                </a>
              </li>
              <li className="nav-item">
                <a 
                  href="#" 
                  className={`nav-link ${activeTab === 'success' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveTab('success'); }}
                >
                  Sucessos
                  <span className="badge bg-green ms-1">{Array.isArray(logs.success) ? logs.success.length : 0}</span>
                </a>
              </li>
            </ul>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Filtrar logs..." 
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  {filter && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setFilter('')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-6 text-end">
                <button 
                  className="btn btn-outline-primary me-2" 
                  onClick={() => exportLogs(activeTab)}
                  disabled={!Array.isArray(logs[activeTab]) || logs[activeTab].length === 0}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                    <line x1="12" y1="11" x2="12" y2="17" />
                    <polyline points="9 14 12 17 15 14" />
                  </svg>
                  Exportar
                </button>
                <button 
                  className="btn btn-outline-danger" 
                  onClick={() => clearLogs(activeTab)}
                  disabled={!Array.isArray(logs[activeTab]) || logs[activeTab].length === 0}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                    <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                  </svg>
                  Limpar
                </button>
              </div>
            </div>
            
            {logsLoading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-3">Carregando logs...</p>
              </div>
            ) : (
              <div className="logs-container">
                {activeTab === 'errors' && (
                  <>
                    {!Array.isArray(logs.errors) || logs.errors.length === 0 ? (
                      <div className="empty">
                        <div className="empty-img">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                          </svg>
                        </div>
                        <p className="empty-title">Nenhum log de erro encontrado</p>
                        <p className="empty-subtitle text-muted">
                          Não há logs de erro registrados no sistema ou o filtro aplicado não retornou resultados.
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table">
                          <thead>
                            <tr>
                              <th>Data/Hora</th>
                              <th>Mensagem</th>
                              <th>Detalhes</th>
                              <th>Fonte</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLogs('errors').map((log, index) => (
                              <tr key={index}>
                                <td className="text-nowrap">{formatDate(log.timestamp)}</td>
                                <td>{log.message}</td>
                                <td>
                                  <div className="text-muted text-wrap" style={{ maxWidth: '400px' }}>
                                    {log.details}
                                  </div>
                                </td>
                                <td>
                                  {log.source && (
                                    <span className={`badge bg-${
                                      log.source === 'database' ? 'blue' : 
                                      log.source === 'email' ? 'purple' : 
                                      'orange'
                                    }`}>
                                      {log.source}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div className="btn-list">
                                    <button 
                                      className="btn btn-sm btn-outline-primary" 
                                      onClick={() => openErrorDetails(log)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <circle cx="12" cy="12" r="2" />
                                        <path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" />
                                      </svg>
                                      Detalhes
                                    </button>
                                    {log.dockerLogLink && (
                                      <button 
                                        className="btn btn-sm btn-outline-info" 
                                        onClick={() => viewDockerLogs(getContainerName(log.dockerLogLink), log.timestamp)}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                                          <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                                          <path d="M10 13l-1 2l1 2" />
                                          <path d="M14 13l1 2l-1 2" />
                                        </svg>
                                        Logs Docker
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
                
                {activeTab === 'success' && (
                  <>
                    {!Array.isArray(logs.success) || logs.success.length === 0 ? (
                      <div className="empty">
                        <div className="empty-img">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                          </svg>
                        </div>
                        <p className="empty-title">Nenhum log de sucesso encontrado</p>
                        <p className="empty-subtitle text-muted">
                          Não há logs de sucesso registrados no sistema ou o filtro aplicado não retornou resultados.
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-vcenter card-table">
                          <thead>
                            <tr>
                              <th>Data/Hora</th>
                              <th>Mensagem</th>
                              <th>Detalhes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLogs('success').map((log, index) => (
                              <tr key={index}>
                                <td className="text-nowrap">{formatDate(log.timestamp)}</td>
                                <td>{log.message}</td>
                                <td>
                                  <div className="text-muted text-wrap" style={{ maxWidth: '400px' }}>
                                    {log.details}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalhes do erro */}
      {selectedError && (
        <div className="modal modal-blur fade show" id="modal-error-details" tabIndex="-1" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Detalhes do Erro</h5>
                <button type="button" className="btn-close" onClick={closeErrorDetails}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h4>{selectedError.message}</h4>
                  <div className="text-muted">
                    {formatDate(selectedError.timestamp)}
                    {selectedError.source && (
                      <span className={`badge ms-2 bg-${
                        selectedError.source === 'database' ? 'blue' : 
                        selectedError.source === 'email' ? 'purple' : 
                        'orange'
                      }`}>
                        {selectedError.source}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="mb-3">
                  <h5>Detalhes</h5>
                  <div className="text-muted">{selectedError.details}</div>
                </div>
                
                {selectedError.stackTrace && (
                  <div className="mb-3">
                    <h5>Stack Trace</h5>
                    <pre className="bg-dark text-white p-3 rounded" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {selectedError.stackTrace}
                    </pre>
                  </div>
                )}
                
                {selectedError.relatedErrors && selectedError.relatedErrors.length > 0 && (
                  <div className="mb-3">
                    <h5>Erros Relacionados</h5>
                    <div className="table-responsive">
                      <table className="table table-vcenter table-sm">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Mensagem</th>
                            <th>Data/Hora</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {findRelatedErrors(selectedError.relatedErrors).map((error, index) => (
                            <tr key={index}>
                              <td>{error.id}</td>
                              <td>{error.message}</td>
                              <td>{formatDate(error.timestamp)}</td>
                              <td>
                                <button 
                                  className="btn btn-sm btn-outline-primary" 
                                  onClick={() => {
                                    closeErrorDetails();
                                    setTimeout(() => openErrorDetails(error), 100);
                                  }}
                                >
                                  Ver
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {selectedError.dockerLogLink && (
                  <div className="mb-3">
                    <h5>Logs do Docker</h5>
                    <button 
                      className="btn btn-outline-info" 
                      onClick={() => viewDockerLogs(getContainerName(selectedError.dockerLogLink), selectedError.timestamp)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                        <path d="M10 13l-1 2l1 2" />
                        <path d="M14 13l1 2l-1 2" />
                      </svg>
                      Ver Logs do Container {getContainerName(selectedError.dockerLogLink)}
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={closeErrorDetails}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Overlay para o modal */}
      {selectedError && (
        <div className="modal-backdrop fade show"></div>
      )}
    </MainLayout>
  );
};

export default ErrorLogs; 